import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useCurrentDataContext } from "../../../contexts/CurrentDataContext";
import { useErrorContext } from "../../../contexts/ErrorContext";
import { useOriginalDataContext } from "../../../contexts/OriginalDataContext";
import { useRoutesContext } from "../../../contexts/RoutesContext";
import { useUserContext } from "../../../contexts/UserContext";
import { updateForm } from "../../../scripts/dataHandlers";
import classes from "./FormPageChangeConfirm.module.css";

interface Props {
  setConfirmNeeded: (val: boolean) => void;
}

const FormPageChangeConfirm = ({ setConfirmNeeded }: Props) => {
  const { user } = useUserContext();

  const { currentData, setCurrentData } = useCurrentDataContext();

  const { setOriginalData } = useOriginalDataContext();

  const { routes } = useRoutesContext();

  const { handleError } = useErrorContext();

  // Set translation
  const { t } = useTranslation();

  const navigate = useNavigate();

  const handleReturnToForm = () => {
    // Set confirmNeeded to false, which removes this modal
    // from rendering process in TopBar component
    setConfirmNeeded(false);
  };

  const handleSaveAndQuit = async () => {
    // Create info message about form saving
    toast(`${t("message.savingForm")}`, {
      toastId: "updateForm",
      position: "top-center",
      autoClose: false,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
    });

    // Save to DB
    const res =
      user &&
      currentData &&
      currentData.employeeId &&
      (await updateForm(
        user.id,
        currentData.employeeId,
        currentData.formId,
        currentData
      ));
    toast.dismiss("updateForm");
    if (res?.status === 200) {
      // If response/save is successful
      // Clear current and original datas
      setOriginalData(undefined);
      setCurrentData(undefined);

      // Close confirmation modal
      setConfirmNeeded(false);

      // Navigate to main view
      navigate(`/${routes.main}`);
    } else {
      handleError(res, "UpdateFormFailed");
    }
  };

  const handleQuitWithoutSaving = () => {
    // Navigate to /main view without saving
    navigate(`/${routes.main}`);

    // Clear current and original datas
    setOriginalData(undefined);
    setCurrentData(undefined);

    // Set confirmNeeded to false, which removes this modal
    // from rendering process in TopBar component
    setConfirmNeeded(false);
  };

  return (
    <div className={classes.confirmBackground}>
      <div className={classes.confirmModal}>
        <div className={classes.confirmTitle}>
          {t("formPageChangeConfirm.title")}
        </div>
        <div className={classes.confirmText}>
          {t("formPageChangeConfirm.infoText")}
        </div>
        <div className={classes.buttonsContainer}>
          <button
            className={classes.returnToFormButton}
            onClick={() => handleReturnToForm()}
          >
            {t("formPageChangeConfirm.returnToForm")}
          </button>
          <button
            className={classes.saveAndQuitButton}
            onClick={() => handleSaveAndQuit()}
          >
            {t("formPageChangeConfirm.saveAndQuit")}
          </button>
        </div>
        <div
          className={classes.quitWithoutSavingButton}
          onClick={() => handleQuitWithoutSaving()}
        >
          {t("formPageChangeConfirm.quitWithoutSaving")}
        </div>
      </div>
    </div>
  );
};

export default FormPageChangeConfirm;
